import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Trophy, Star, Zap, Target, Download, CheckCircle,
  TrendingUp, Award, ChevronLeft, FileText, BarChart3
} from 'lucide-react';
import PillarRadarChart from '../components/PillarRadarChart';
import { PILLAR_NAMES } from '../data/pillars';

const ResultsPage = () => {
  const { assessmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [results, setResults] = useState(null);
  const [peer, setPeer] = useState(null);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const organizationId = location.state?.organizationId || localStorage.getItem('organization_id') || 'b8b52a08-165e-4a21-85ae-099e7268a29e';

  useEffect(() => {
    fetchResults();
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organization/${organizationId}/pillar-radar`)
      .then((r) => r.json())
      .then((d) => setPeer(d.peer_comparison))
      .catch(() => setPeer(null));
  }, []);

  const fetchResults = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/assessment-results/${assessmentId}/${organizationId}`);
      if (response.ok) {
        const data = await response.json();
        setResults(data);
      }
    } catch (error) {
      console.error('Error fetching results:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTierBadge = (tierName) => {
    const badges = {
      Luminary: {
        icon: Star,
        gradient: 'from-yellow-400 via-yellow-500 to-yellow-600',
        color: '#FFD700',
        textColor: 'text-yellow-900',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-500',
        description: '🌟 Top-tier certification'
      },
      Inspired: {
        icon: Zap,
        gradient: 'from-gray-300 via-gray-400 to-gray-500',
        color: '#C0C0C0',
        textColor: 'text-gray-900',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-400',
        description: '⚡ Strong performance certification'
      },
      Aspiring: {
        icon: Target,
        gradient: 'from-orange-400 via-orange-500 to-orange-600',
        color: '#CD7F32',
        textColor: 'text-orange-900',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-500',
        description: '🎯 Emerging organization'
      }
    };
    return badges[tierName] || badges.Aspiring;
  };

  const getPerformanceColor = (level) => {
    const colors = {
      exceptional: 'bg-green-100 text-green-800',
      strong: 'bg-blue-100 text-blue-800',
      developing: 'bg-yellow-100 text-yellow-800',
      emerging: 'bg-orange-100 text-orange-800',
      insufficient: 'bg-red-100 text-red-800'
    };
    return colors[level] || colors.developing;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your results...</p>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Results Not Available</h2>
          <p className="text-gray-600 mb-6">Your assessment is still under evaluation.</p>
          <button
            onClick={() => navigate('/org-admin/assessments')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Safe access with defaults
  const certificationTier = results.certification_tier || 'Aspiring';
  const performanceCategory = results.performance_category || 'developing';
  const overallScore = results.overall_score || 0;
  const tierThreshold = results.tier_threshold_met || {};

  const tierBadge = getTierBadge(certificationTier);
  const TierIcon = tierBadge.icon;

  // Six-pillar radar data (current pillar names, active pillars only)
  const radarPillars = (results.pillar_results || [])
    .filter((p) => PILLAR_NAMES[p.pillar_number])
    .map((p) => ({
      pillar_number: p.pillar_number,
      name: PILLAR_NAMES[p.pillar_number],
      score: Math.round(p.total_score || 0),
    }));
  const weakestPillar = radarPillars.length
    ? radarPillars.reduce((min, p) => (p.score < min.score ? p : min), radarPillars[0])
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className={`bg-gradient-to-r ${tierBadge.gradient} text-white`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/org-admin/assessments')}
              className="p-2 hover:bg-white/20 rounded-lg transition-all"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-3xl font-black">Assessment Results</h1>
              <p className="text-white/80">Your CNESS Certification Results</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Certification Badge Card */}
        <div className={`bg-gradient-to-br ${tierBadge.gradient} rounded-3xl shadow-2xl overflow-hidden mb-8`}>
          <div className="p-12 text-center text-white">
            <div className="inline-flex items-center justify-center w-32 h-32 bg-white/20 backdrop-blur-sm rounded-full mb-6">
              <TierIcon className="w-16 h-16" />
            </div>
            <h2 className="text-5xl font-black mb-3">🎉 CERTIFICATION ACHIEVED!</h2>
            <div className="inline-block bg-white/30 backdrop-blur-sm px-8 py-4 rounded-2xl mb-6">
              <p className="text-6xl font-black">{certificationTier.toUpperCase()}</p>
            </div>
            <p className="text-2xl mb-2">{tierBadge.description}</p>
            <p className="text-xl opacity-90">Overall Score: {overallScore}/100</p>
            
            <div className="mt-8 flex items-center justify-center gap-4">
              <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-lg">
                <p className="text-sm opacity-80">Certificate Number</p>
                <p className="text-lg font-bold">{results.certificate_number}</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-lg">
                <p className="text-sm opacity-80">Issued Date</p>
                <p className="text-lg font-bold">{new Date(results.certificate_issued_at).toLocaleDateString()}</p>
              </div>
            </div>

            <div className="mt-8 flex gap-4 justify-center">
              <button className="bg-white text-gray-900 px-8 py-4 rounded-xl font-bold hover:bg-gray-100 transition-all flex items-center gap-2 shadow-lg">
                <Download className="w-5 h-5" />
                Download Certificate
              </button>
              <button className="bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-bold hover:bg-white/30 transition-all flex items-center gap-2">
                <Award className="w-5 h-5" />
                View Badge
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Score Breakdown */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overall Performance */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-2">
                <BarChart3 className="w-7 h-7 text-blue-600" />
                Performance Overview
              </h3>
              
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-lg font-bold text-gray-900">Overall Score</span>
                  <span className="text-3xl font-black text-blue-600">{overallScore}/100</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-6">
                  <div
                    className={`bg-gradient-to-r ${tierBadge.gradient} h-6 rounded-full transition-all flex items-center justify-end pr-3`}
                    style={{ width: `${overallScore}%` }}
                  >
                    <span className="text-xs font-bold text-white">{overallScore}%</span>
                  </div>
                </div>
                <div className="mt-2">
                  <span className={`inline-block px-4 py-2 rounded-full text-sm font-bold ${getPerformanceColor(performanceCategory)}`}>
                    {performanceCategory.toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Six-Pillar Radar */}
              {radarPillars.length > 0 && (
                <div className="mb-10 border border-gray-200 rounded-xl p-6 bg-gray-50/60">
                  <h4 className="text-lg font-bold text-gray-900 mb-6">Six-Pillar Profile</h4>
                  <PillarRadarChart
                    pillars={radarPillars}
                    weakest={weakestPillar}
                    hint={weakestPillar ? `Your lowest pillar is ${weakestPillar.name} at ${weakestPillar.score}. Focus your next improvement cycle here to lift the overall CIS score fastest.` : null}
                    overallScore={overallScore}
                    peer={peer}
                  />
                </div>
              )}

              {/* Pillar Scores */}
              <div className="space-y-4">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Pillar-wise Scores</h4>
                {(results.pillar_results || []).filter((p) => PILLAR_NAMES[p.pillar_number]).map((pillar) => (
                  <div key={pillar.pillar_number} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-all">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex-1">
                        <span className="font-bold text-gray-900">Pillar {pillar.pillar_number}: {PILLAR_NAMES[pillar.pillar_number]}</span>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getPerformanceColor(pillar.performance_level)}`}>
                            {pillar.performance_level}
                          </span>
                          <span className="text-sm text-gray-600">{pillar.questions_scored}/{pillar.total_questions} questions</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-black text-blue-600">{pillar.total_score}</p>
                        <p className="text-xs text-gray-500">out of 100</p>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all"
                        style={{ width: `${pillar.total_score}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Evaluator Feedback */}
            {(results.overall_comments || results.recommendations) && (
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-2">
                  <FileText className="w-7 h-7 text-purple-600" />
                  Evaluator Feedback
                </h3>
                
                {results.overall_comments && (
                  <div className="mb-6">
                    <h4 className="font-bold text-gray-900 mb-2">Overall Comments</h4>
                    <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{results.overall_comments}</p>
                  </div>
                )}

                {results.recommendations && (
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Recommendations</h4>
                    <p className="text-gray-700 bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">{results.recommendations}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Summary */}
          <div className="space-y-6">
            {/* Tier Info Card */}
            <div className={`${tierBadge.bgColor} border-2 ${tierBadge.borderColor} rounded-2xl p-6`}>
              <div className="flex items-center gap-3 mb-4">
                <TierIcon className="w-8 h-8" style={{ color: tierBadge.color }} />
                <h3 className="text-xl font-black" style={{ color: tierBadge.color }}>
                  {certificationTier} Tier
                </h3>
              </div>
              <p className={`text-sm ${tierBadge.textColor} mb-4`}>{results.tier_description || tierBadge.description}</p>
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-600">Score Range</p>
                <p className="text-sm font-bold">
                  {tierThreshold[`${certificationTier.toLowerCase()}_min`] || 0}+ points
                </p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h4 className="font-bold text-gray-900 mb-4">Assessment Summary</h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                  <span className="text-sm text-gray-600">Organization</span>
                  <span className="text-sm font-bold text-gray-900">{results.organization_name}</span>
                </div>
                <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                  <span className="text-sm text-gray-600">Evaluator</span>
                  <span className="text-sm font-bold text-gray-900">{results.evaluator_name}</span>
                </div>
                <div className="flex items-center justify-between pb-3 border-b border-gray-200">
                  <span className="text-sm text-gray-600">Evaluation Date</span>
                  <span className="text-sm font-bold text-gray-900">
                    {new Date(results.evaluation_completed_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Certificate ID</span>
                  <span className="text-xs font-mono text-gray-900">{results.certificate_number}</span>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 border-2 border-blue-200">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Next Steps
              </h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Download your certificate</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Display your badge on website</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Share results with stakeholders</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Join CNESS community network</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;
